import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PrivacyLinks from "@/components/PrivacyLinks";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";
import {
  ArrowLeft, User, Building2, FileText, Upload, Check, ChevronRight, ChevronLeft,
  CheckCircle2, AlertCircle, Copy, Paperclip, X as XIcon
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 0, label: "Tipologia", icon: User },
  { id: 1, label: "Contatti", icon: User },
  { id: 2, label: "Dati anagrafici", icon: FileText },
  { id: 3, label: "Economica", icon: Building2 },
  { id: 4, label: "Documenti", icon: Upload },
  { id: 5, label: "Conferma", icon: CheckCircle2 },
];

function FileUploader({ files, onChange, max = 10, label }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (list) => {
    if (!list?.length) return;
    setUploading(true);
    for (const f of Array.from(list).slice(0, max - files.length)) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max 10MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await axios.post(`${API}/upload`, fd);
        onChange([...files, { id: data.id, name: f.name, size: f.size, label }]);
      } catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
    }
    setUploading(false);
  };
  return (
    <div>
      <div onClick={() => document.getElementById(`file-${label}`).click()}
        className="border-2 border-dashed border-[#E5E2DB] rounded-xl p-4 text-center cursor-pointer hover:border-[#163321]/40 hover:bg-[#F5F8F6]/50 transition-all">
        <Upload className="w-5 h-5 text-[#4A5E51] mx-auto mb-1.5" />
        <p className="text-xs text-[#0F1F14] font-medium">{uploading ? "Caricamento..." : label}</p>
        <p className="text-[10px] text-[#4A5E51]">Clicca o trascina • PDF/JPG/PNG max 10MB</p>
        <input id={`file-${label}`} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf" className="hidden" onChange={(e) => handle(e.target.files)} />
      </div>
      {files.filter(f => f.label === label).length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.filter(f => f.label === label).map(f => (
            <li key={f.id} className="flex items-center gap-2 p-1.5 bg-[#F5F8F6] rounded-lg text-xs">
              <Paperclip className="w-3 h-3 text-[#163321]" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-[10px] text-[#4A5E51]">{(f.size / 1024).toFixed(0)} KB</span>
              <button type="button" onClick={() => onChange(files.filter(x => x.id !== f.id))} className="p-0.5 hover:bg-white rounded">
                <XIcon className="w-3 h-3 text-[#4A5E51]" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PraticaNoleggioPage() {
  const { carId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // Parametri personalizzati scelti dal cliente in CarDetailPage (slider km + durata)
  const customKm = useMemo(() => { const v = parseInt(sp.get("km") || "", 10); return Number.isFinite(v) && v > 0 ? v : null; }, [sp]);
  const customDurata = useMemo(() => { const v = parseInt(sp.get("durata") || "", 10); return Number.isFinite(v) && v > 0 ? v : null; }, [sp]);
  const isCustomConfig = sp.get("custom") === "1";

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    tipo_cliente: "privato",
    durata_mesi: customDurata || 36,
    km_annui: customKm || 15000,
    anticipo: 0,
    contatto: { nome: "", cognome: "", email: "", telefono: "" },
    dati_fiscali: { codice_fiscale: "", data_nascita: "", luogo_nascita: "", indirizzo: "", citta: "", cap: "", provincia: "" },
    dati_azienda: { ragione_sociale: "", partita_iva: "", codice_sdi: "", pec: "", settore: "", anni_attivita: 0 },
    dati_reddito: { tipo_impiego: "", datore_lavoro: "", reddito_mensile: 0, anni_impiego: 0 },
  });
  const [files, setFiles] = useState([]);
  // Informativa privacy precompilata di default: l'utente può deselezionare se non è d'accordo.
  // Conforme GDPR perché si tratta di una base contrattuale (art. 6.1.b) e la spunta resta liberamente revocabile.
  const [privacy, setPrivacy] = useState(true);

  useEffect(() => {
    // Usa catalogo unificato (Arval + Ayvens) per trovare l'auto
    axios.get(`${API}/noleggio/catalog?limit=2000`).then(r => {
      const cars = Array.isArray(r.data) ? r.data : (r.data.cars || []);
      const found = cars.find(c => String(c.id) === String(carId));
      if (found) {
        // Normalizzazione: unisco lo schema Arval/Ayvens
        const dur = found.durationMonths ?? found.duration ?? 48;
        const kmYear = found.kmPerYear ?? 10000;
        const mileage = found.mileage ?? kmYear;
        const providerRaw = (found.source || found.provider || "").toLowerCase();
        const providerLabel = providerRaw === "arval" ? "Arval" : providerRaw === "ayvens" ? "Ayvens" : (found.provider || "Noleggio");
        const normalized = {
          ...found,
          duration: dur,
          mileage,
          advancedPayment: found.advancedPayment ?? found.deposit ?? 0,
          provider: providerLabel,
          imageUrl: (found.imageUrl || "").startsWith("/api/")
            ? `${process.env.REACT_APP_BACKEND_URL}${found.imageUrl}`
            : found.imageUrl,
        };
        setCar(normalized);
        // Se l'utente ha personalizzato durata/km in CarDetailPage li usa, altrimenti defaults dell'auto
        setForm(f => ({
          ...f,
          durata_mesi: customDurata || normalized.duration || 36,
          km_annui: customKm || normalized.mileage || 15000,
        }));
      } else {
        setCar(null);
      }
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [carId, customDurata, customKm]);

  const upd = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const validStep = () => {
    if (step === 0) return !!form.tipo_cliente;
    if (step === 1) {
      const c = form.contatto;
      return c.nome && c.cognome && /\S+@\S+\.\S+/.test(c.email) && c.telefono.length >= 8;
    }
    if (step === 2) {
      const d = form.dati_fiscali;
      if (form.tipo_cliente === "privato") {
        return d.codice_fiscale.length === 16 && d.indirizzo && d.citta && d.cap && d.provincia;
      } else {
        return form.dati_azienda.ragione_sociale && form.dati_azienda.partita_iva.length >= 11;
      }
    }
    if (step === 3) {
      if (form.tipo_cliente === "privato") return form.dati_reddito.tipo_impiego && form.dati_reddito.reddito_mensile > 0;
      return form.dati_azienda.anni_attivita >= 0;
    }
    if (step === 4) {
      // almeno CI + CF per privato; almeno Visura + CI rapp per azienda
      const labels = new Set(files.map(f => f.label));
      if (form.tipo_cliente === "privato") return labels.has("Carta d'Identita'") && labels.has("Codice Fiscale");
      return labels.has("Visura Camerale") && labels.has("Carta d'Identita' Rappresentante");
    }
    if (step === 5) return privacy;
    return true;
  };

  const submit = async () => {
    if (!privacy) return toast.error("Accetta l'informativa privacy");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/noleggio/pratica`, {
        car_id: car.id,
        car_info: {
          brand: car.brand, model: car.model, provider: car.provider,
          imageUrl: car.imageUrl, canone_mensile_base: car.fee,
          km_base: car.mileage, durata_base: car.duration,
        },
        durata_mesi: form.durata_mesi,
        km_annui: form.km_annui,
        anticipo: Number(form.anticipo) || 0,
        tipologia_richiesta: isCustomConfig ? "quotazione_personalizzata" : "conferma_offerta_standard",
        configurazione_personalizzata: isCustomConfig,
        tipo_cliente: form.tipo_cliente,
        contatto: form.contatto,
        dati_fiscali: form.tipo_cliente === "privato" ? form.dati_fiscali : { ...form.dati_fiscali },
        dati_azienda: form.tipo_cliente !== "privato" ? form.dati_azienda : {},
        dati_reddito: form.tipo_cliente === "privato" ? form.dati_reddito : {},
        file_ids: files.map(f => f.id),
        privacy_accept: true,
      });
      setSuccess(data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally { setSubmitting(false); }
  };

  if (loading) return <><Header /><div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" /></div></>;
  if (!car) return <><Header /><div className="min-h-screen pt-20 text-center text-[#4A5E51]">Auto non trovata. <Link to="/noleggio" className="text-[#163321] underline">Torna al catalogo</Link></div></>;

  const fmt = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

  if (success) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="pratica-success-page">
          <div className="max-w-2xl mx-auto px-5 py-16 text-center">
            <div className="w-20 h-20 bg-[#163321]/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-[#163321]" />
            </div>
            <h1 className="font-heading text-3xl text-[#0F1F14] font-medium mb-3">Richiesta di quotazione ricevuta</h1>
            <p className="text-sm text-[#4A5E51] mb-8 max-w-md mx-auto">
              Abbiamo ricevuto la tua richiesta di quotazione per <strong>{car.brand} {car.model}</strong>. Un nostro consulente preparerà una proposta su misura e ti contatterà entro 24 ore lavorative.
            </p>
            <div className="bg-white border-2 border-[#163321]/20 rounded-2xl p-6 max-w-sm mx-auto mb-8">
              <p className="text-[10px] uppercase tracking-wider text-[#4A5E51] mb-1">Numero richiesta</p>
              <p className="font-mono text-xl font-bold text-[#163321]" data-testid="pratica-numero">{success.numero_pratica}</p>
              <Button onClick={() => { navigator.clipboard.writeText(success.numero_pratica); toast.success("Copiato"); }} variant="ghost" size="sm" className="mt-2 text-xs text-[#163321]">
                <Copy className="w-3 h-3 mr-1" /> Copia
              </Button>
            </div>
            <div className="flex gap-3 justify-center">
              <Link to="/noleggio"><Button variant="outline" className="rounded-full border-[#E5E2DB]">Torna al catalogo</Button></Link>
              <Link to="/"><Button className="bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-full px-6">Home</Button></Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="pratica-noleggio-page">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <Link to={`/noleggio/${carId}`} className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Torna all'auto
          </Link>

          {/* Riepilogo auto */}
          <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4 mb-2 flex items-center gap-4">
            {car.imageUrl && <img src={car.imageUrl} alt={car.model} className="w-24 h-16 object-contain" />}
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-[#4A5E51]">{car.brand}</p>
              <p className="font-heading text-base font-medium text-[#0F1F14]">{car.model}</p>
              <p className="text-xs text-[#4A5E51]">{form.durata_mesi} mesi · {form.km_annui.toLocaleString("it-IT")} km/anno</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#4A5E51]">Canone</p>
              {isCustomConfig ? (
                <p className="font-heading text-sm font-bold text-[#D97B58]">Da quotare</p>
              ) : (
                <p className="font-heading text-lg font-bold text-[#163321]">{car.fee ? fmt(car.fee) : "—"}</p>
              )}
              <p className="text-[10px] text-[#4A5E51]">+IVA/mese</p>
            </div>
          </div>

          {/* Banner configurazione personalizzata */}
          {isCustomConfig && (
            <div className="bg-[#D97B58]/10 border border-[#D97B58]/30 rounded-xl px-4 py-2.5 mb-5 flex items-center gap-2" data-testid="custom-config-banner">
              <AlertCircle className="w-4 h-4 text-[#D97B58] shrink-0" />
              <p className="text-xs text-[#0F1F14]">
                Configurazione personalizzata: <strong>{form.km_annui.toLocaleString("it-IT")} km/anno</strong> per <strong>{form.durata_mesi} mesi</strong>. Il nostro consulente ti invierà una quotazione su misura entro 24h.
              </p>
            </div>
          )}
          {!isCustomConfig && <div className="mb-5" />}

          {/* Progress */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] whitespace-nowrap ${i === step ? "bg-[#163321] text-white" : i < step ? "bg-[#163321]/10 text-[#163321]" : "text-[#4A5E51]"}`}>
                {i < step ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 inline-flex items-center justify-center text-[9px]">{i + 1}</span>}
                {s.label}
              </div>
            ))}
          </div>

          {/* STEP CONTENT */}
          <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6 min-h-[280px]">

            {/* STEP 0 - Tipologia */}
            {step === 0 && (
              <div data-testid="step-tipologia">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-4">Tipologia cliente</h2>
                <RadioGroup value={form.tipo_cliente} onValueChange={(v) => upd("tipo_cliente", v)} className="space-y-2">
                  {[
                    { v: "privato", l: "Privato", d: "Persona fisica, dipendente o pensionato" },
                    { v: "partita_iva", l: "Partita IVA / Libero Professionista", d: "Consulente, medico, avvocato, artigiano..." },
                    { v: "azienda", l: "Azienda / SRL / SAS", d: "Societa' di capitali o di persone" },
                  ].map(o => (
                    <label key={o.v} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${form.tipo_cliente === o.v ? "border-[#163321] bg-[#F5F8F6]" : "border-[#E5E2DB]"}`}>
                      <RadioGroupItem value={o.v} className="mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-[#0F1F14]">{o.l}</p>
                        <p className="text-xs text-[#4A5E51]">{o.d}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* STEP 1 - Contatti */}
            {step === 1 && (
              <div data-testid="step-contatti">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-4">Dati di contatto</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nome *</Label><Input value={form.contatto.nome} onChange={e => upd("contatto.nome", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                  <div><Label className="text-xs">Cognome *</Label><Input value={form.contatto.cognome} onChange={e => upd("contatto.cognome", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                  <div><Label className="text-xs">Email *</Label><Input type="email" value={form.contatto.email} onChange={e => upd("contatto.email", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                  <div><Label className="text-xs">Telefono *</Label><Input type="tel" value={form.contatto.telefono} onChange={e => upd("contatto.telefono", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                </div>
              </div>
            )}

            {/* STEP 2 - Anagrafica */}
            {step === 2 && (
              <div data-testid="step-anagrafica">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-4">
                  {form.tipo_cliente === "privato" ? "Dati anagrafici e residenza" : "Dati aziendali"}
                </h2>
                {form.tipo_cliente === "privato" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-full"><Label className="text-xs">Codice Fiscale *</Label><Input maxLength={16} value={form.dati_fiscali.codice_fiscale} onChange={e => upd("dati_fiscali.codice_fiscale", e.target.value.toUpperCase())} className="mt-1 h-10 rounded-xl font-mono uppercase" /></div>
                    <div><Label className="text-xs">Data di nascita *</Label><Input type="date" value={form.dati_fiscali.data_nascita} onChange={e => upd("dati_fiscali.data_nascita", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Luogo di nascita</Label><ComuneAutocomplete value={form.dati_fiscali.luogo_nascita} onChange={(v) => upd("dati_fiscali.luogo_nascita", v)} className="mt-1" /></div>
                    <div className="col-span-full"><Label className="text-xs">Indirizzo di residenza *</Label><Input value={form.dati_fiscali.indirizzo} onChange={e => upd("dati_fiscali.indirizzo", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Citta' *</Label><Input value={form.dati_fiscali.citta} onChange={e => upd("dati_fiscali.citta", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">CAP *</Label><Input maxLength={5} value={form.dati_fiscali.cap} onChange={e => upd("dati_fiscali.cap", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Provincia *</Label><Input maxLength={2} value={form.dati_fiscali.provincia} onChange={e => upd("dati_fiscali.provincia", e.target.value.toUpperCase())} className="mt-1 h-10 rounded-xl uppercase" /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-full"><Label className="text-xs">Ragione sociale *</Label><Input value={form.dati_azienda.ragione_sociale} onChange={e => upd("dati_azienda.ragione_sociale", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Partita IVA *</Label><Input maxLength={11} value={form.dati_azienda.partita_iva} onChange={e => upd("dati_azienda.partita_iva", e.target.value)} className="mt-1 h-10 rounded-xl font-mono" /></div>
                    <div><Label className="text-xs">Codice SDI</Label><Input maxLength={7} value={form.dati_azienda.codice_sdi} onChange={e => upd("dati_azienda.codice_sdi", e.target.value.toUpperCase())} className="mt-1 h-10 rounded-xl font-mono uppercase" /></div>
                    <div className="col-span-full"><Label className="text-xs">PEC</Label><Input type="email" value={form.dati_azienda.pec} onChange={e => upd("dati_azienda.pec", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Settore</Label><Input value={form.dati_azienda.settore} onChange={e => upd("dati_azienda.settore", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div><Label className="text-xs">Anni attivita'</Label><Input type="number" min={0} value={form.dati_azienda.anni_attivita} onChange={e => upd("dati_azienda.anni_attivita", parseInt(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" /></div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 - Economica */}
            {step === 3 && (
              <div data-testid="step-economica">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-4">Informazioni economiche</h2>
                {form.tipo_cliente === "privato" ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Tipo impiego *</Label>
                      <Select value={form.dati_reddito.tipo_impiego} onValueChange={v => upd("dati_reddito.tipo_impiego", v)}>
                        <SelectTrigger className="mt-1 rounded-xl h-10"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dipendente">Dipendente a tempo indeterminato</SelectItem>
                          <SelectItem value="dipendente_det">Dipendente a tempo determinato</SelectItem>
                          <SelectItem value="autonomo">Libero professionista</SelectItem>
                          <SelectItem value="pensionato">Pensionato</SelectItem>
                          <SelectItem value="altro">Altro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Datore di lavoro</Label><Input value={form.dati_reddito.datore_lavoro} onChange={e => upd("dati_reddito.datore_lavoro", e.target.value)} className="mt-1 h-10 rounded-xl" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Reddito mensile netto (€) *</Label><Input type="number" min={0} value={form.dati_reddito.reddito_mensile} onChange={e => upd("dati_reddito.reddito_mensile", parseFloat(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" /></div>
                      <div><Label className="text-xs">Anni impiego attuale</Label><Input type="number" min={0} value={form.dati_reddito.anni_impiego} onChange={e => upd("dati_reddito.anni_impiego", parseInt(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" /></div>
                    </div>
                    <div><Label className="text-xs">Anticipo a tuo carico (€)</Label><Input type="number" min={0} value={form.anticipo} onChange={e => upd("anticipo", parseFloat(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" /></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-[#4A5E51]">Per aziende e P.IVA valuteremo la pratica con il partner noleggiatore sulla base della visura e degli ultimi bilanci/730 da caricare al prossimo step.</p>
                    <div><Label className="text-xs">Anticipo previsto (€)</Label><Input type="number" min={0} value={form.anticipo} onChange={e => upd("anticipo", parseFloat(e.target.value) || 0)} className="mt-1 h-10 rounded-xl" /></div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4 - Documenti */}
            {step === 4 && (
              <div data-testid="step-documenti">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-2">Documenti richiesti</h2>
                <p className="text-xs text-[#4A5E51] mb-4">I documenti caricati vengono trasmessi in forma cifrata al partner noleggiatore per la valutazione.</p>
                <div className="space-y-3">
                  {form.tipo_cliente === "privato" ? (
                    <>
                      <FileUploader files={files} onChange={setFiles} label="Carta d'Identita'" />
                      <FileUploader files={files} onChange={setFiles} label="Codice Fiscale" />
                      <FileUploader files={files} onChange={setFiles} label="Ultime 2 Buste Paga / CUD / 730" />
                      <FileUploader files={files} onChange={setFiles} label="Patente di Guida" />
                    </>
                  ) : (
                    <>
                      <FileUploader files={files} onChange={setFiles} label="Visura Camerale" />
                      <FileUploader files={files} onChange={setFiles} label="Carta d'Identita' Rappresentante" />
                      <FileUploader files={files} onChange={setFiles} label="Ultimi 2 Bilanci / 730" />
                      <FileUploader files={files} onChange={setFiles} label="Codice Fiscale Rappresentante" />
                    </>
                  )}
                </div>
              </div>
            )}

            {/* STEP 5 - Conferma */}
            {step === 5 && (
              <div data-testid="step-conferma">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-4">Conferma e invia</h2>
                <div className="bg-[#F5F8F6] rounded-xl p-4 space-y-2 text-xs">
                  <p><strong>Auto:</strong> {car.brand} {car.model}</p>
                  <p><strong>Configurazione:</strong> {form.durata_mesi} mesi · {form.km_annui.toLocaleString("it-IT")} km/anno · Canone {fmt(car.fee)}/mese</p>
                  <p><strong>Tipologia:</strong> {form.tipo_cliente === "privato" ? "Privato" : form.tipo_cliente === "partita_iva" ? "Partita IVA" : "Azienda"}</p>
                  <p><strong>Contatto:</strong> {form.contatto.nome} {form.contatto.cognome} · {form.contatto.email} · {form.contatto.telefono}</p>
                  <p><strong>Documenti caricati:</strong> {files.length}</p>
                </div>
                <div className="mt-5 p-3 rounded-xl bg-[#F5F8F6] border border-[#163321]/10 text-[11px] text-[#4A5E51] flex items-start gap-2" data-testid="precompilato-hint">
                  <span className="font-semibold text-[#163321]">✓ Informative precompilate</span>
                  <span>— Per velocizzare la pratica, l'informativa privacy è già spuntata. Puoi deselezionarla qui sotto se non sei d'accordo.</span>
                </div>
                <label className="flex items-start gap-3 mt-3 cursor-pointer">
                  <Checkbox checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5" data-testid="pratica-privacy" />
                  <span className="text-xs text-[#4A5E51]">
                    Ho letto l'<Link to="/privacy" target="_blank" className="text-[#163321] underline">Informativa Privacy</Link> e acconsento al trattamento dei dati per la gestione della presente pratica di noleggio e la trasmissione al partner noleggiatore (Ayvens/Arval).
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* NAVIGATION */}
          <div className="flex items-center justify-between mt-5 gap-3">
            <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="rounded-full border-[#E5E2DB]" data-testid="pratica-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => { if (validStep()) setStep(s => s + 1); else toast.error("Completa i campi obbligatori"); }} className="bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-full px-6" data-testid="pratica-next">
                Continua <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting || !privacy} className="bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-full px-6" data-testid="pratica-submit">
                {submitting ? "Invio..." : "Invia richiesta quotazione"}
              </Button>
            )}
          </div>

          <div className="mt-8">
            <PrivacyLinks />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
